import { useMemo, useState } from 'react'
import MFAccordion from '../../ui-components/MFAccordion'
import AccordionHeaderContentWithTag from '../../AccordionHeaderContentWithTag'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../../../constants'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { MetaDataResponse } from '../../../types/mutual-fund-detail.types'
import TrailingReturnsBody from './TRBody'
import { convertUnitAndValue } from '../../../utils/utils'
import { getChangeDirection } from '../../../utils/mutualFundDetail'
import { EventNames, UNITS } from '../../../types/misc.types'
import { trackEvent } from '../../../utils/appBridge'

const BASE_TRAILING_RETURNS_DATA: MetaDataResponse['trailingReturns'] = {
  tenure: {
    value: 0,
    period: 'YEAR'
  },
  returns: {
    unit: 'PERCENTAGE',
    value: 0
  },
  oneDayReturn: 0,
  principal: 0,
  returnsSinceInception: 0
}

export default function TrailingReturnsSection() {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const { data, isin } = useMetaDataContext()
  const trailingReturns = useMemo(() => {
    return data?.trailingReturns ?? BASE_TRAILING_RETURNS_DATA
  }, [data])

  const getTitle = () => {
    if (trailingReturns?.tenure)
      return `${convertUnitAndValue({
        value: trailingReturns?.tenure?.value ?? 0,
        unit: trailingReturns?.tenure?.period ?? UNITS.MONTH,
        format: {
          casing: 'PASCAL',
          type: 'SHORT'
        }
      })} Trailing Returns`
    return 'Returns So Far'
  }

  const getChangeText = () => {
    const oneDayReturn = Math.abs(trailingReturns?.oneDayReturn)
    return `${convertUnitAndValue({
      value: oneDayReturn ?? 0,
      unit: UNITS.PERCENTAGE
    })} in 1D`
  }

  const getSectionValue = () => {
    if (trailingReturns?.returns)
      return `${convertUnitAndValue({
        value: trailingReturns?.returns?.value ?? 0,
        unit: trailingReturns?.returns?.unit ?? UNITS.PERCENTAGE
      })}`
    return `${convertUnitAndValue({
      value: trailingReturns?.returnsSinceInception ?? 0,
      unit: UNITS.PERCENTAGE
    })}`
  }

  const handleAccordionClick = () =>
    setIsAccordionOpen((isAccordionOpenPrev) => {
      trackEvent({
        eventName: EventNames.UserClicked,
        payload: {
          eventId: 'trailing_returns',
          responseName: isAccordionOpenPrev ? 'Collapse' : 'Expand',
          screenType: 'MF_L2',
          fundName: isin
        }
      })
      return !isAccordionOpenPrev
    })

  return (
    <div className='bg-white'>
      <MFAccordion
        isExpanded={isAccordionOpen}
        onAccordionClick={handleAccordionClick}
        icon={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/trailing-returns-icon.svg`}
        title={getTitle()}
        accessibleName={`${getTitle()}, ${getSectionValue()}, ${getChangeText()}`}
        middleContent={
          <AccordionHeaderContentWithTag
            title={getTitle()}
            value={getSectionValue()}
            changeDirection={getChangeDirection(trailingReturns?.oneDayReturn)}
            changeText={getChangeText()}
          />
        }
        chevronTopPadding='12px'
        isBorderTop={data?.sectionAvailability?.rollingReturns !== 'NO'}
      >
        <TrailingReturnsBody />
      </MFAccordion>
    </div>
  )
}
